import { useEffect, useState } from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Button, Image, Skeleton, Spin } from 'antd';
import { ArrowLeftIcon, CallIcon } from 'icons';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { openAuthModal, resetRedirectUrl } from 'modules/auth';
import { UniversitySelectModal } from 'modules/blok-test';
import {
  getUniversityCourseSelect,
  globalConst,
  isEmptyArr,
  useInteractionHandler,
  UserInteraction,
  useUniversityCourseSelect
} from 'modules/global';
import {
  ExtraUniversityList,
  FirstSection,
  hasUniversityTest,
  InfoSection,
  MainInformation,
  UniversityStatistics
} from 'modules/private-universities';
import {
  ContactSection,
  DirectionCard,
  getUniversityByName,
  IAllUniversities,
  universitiesConst,
  universitySchemaJson,
  useUniversityByName
} from 'modules/universities';

import { ApplicationModal, EmptyData, NextHead, Visible, YandexMap } from 'components';

const Index = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const { query, asPath } = useRouter();
  const { university, isLoading } = useUniversityByName(query.universityId as string);
  const { onInteractionClick } = useInteractionHandler(university?.id);
  const [courseId, setCourseId] = useState<string>();
  const [openApplicationCreate, setOpenApplicationCreate] = useState(false);
  const isAuthorized = useAppSelector(({ user }) => user.phone);

  const onOpenModal = (id?: string) => {
    setOpen(true);
    setCourseId(id);
  };

  const { univerCourse, isLoading: isCourseLoading } = useUniversityCourseSelect(university?.id);
  const testAvailable = hasUniversityTest(university?.id);

  const onClick = () => {
    if (testAvailable) {
      return setOpen(true);
    }

    if (!isAuthorized) {
      return dispatch(openAuthModal());
    }

    return setOpenApplicationCreate(true);
  };

  useEffect(() => {
    dispatch(resetRedirectUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(universitySchemaJson(university!, asPath))
          }}
        />
      </Head>
      <NextHead
        title={university?.name}
        banner={university?.image}
        description={university?.description}
        keywords={university?.keywords}
      />

      <div className="max-w-screen mx-auto p-2 lg:my-3 lg:p-3">
        <Link
          href={globalConst.pageNames.PRIVATE_UNIVERSITIES}
          className="inline-flex cursor-pointer items-center justify-start gap-2 pl-4 font-semibold md:pl-0 lg:mb-4"
        >
          <ArrowLeftIcon /> {t('common.ortga_qaytish')}
        </Link>

        <div className="grid grid-cols-1 gap-y-4 p-2 lg:grid-cols-4 lg:gap-4 lg:gap-y-0 lg:p-0">
          <div className="space-y-4 sm:col-span-3">
            <FirstSection university={university} loading={isLoading} />
            {(university?.admission_start_date && university?.admission_end_date) ||
            (university?.max_contract_price && university?.min_contract_price) ||
            university?.file?.name ? (
              <div className="rounded-xl bg-white p-3 md:hidden">
                <ExtraUniversityList
                  admissionStartDate={university?.admission_start_date}
                  admissionEndDate={university?.admission_end_date}
                  maxContractPrice={university?.max_contract_price}
                  minContractPrice={university?.min_contract_price}
                  file={university?.file?.name?.trimEnd()}
                />
              </div>
            ) : null}
            <div className="rounded-xl bg-white p-3 md:hidden">
              <UniversityStatistics
                studentsCount={university?.students_count}
                graduateStudentsCount={university?.graduate_students_count}
                experienceYearsCount={university?.experience_years_count}
                directionCount={university?.direction_count}
              />
            </div>

            <Visible
              if={isCourseLoading && university?.id}
              else={
                <Visible
                  if={isEmptyArr(univerCourse)}
                  else={
                    <>
                      <div className="flex items-center gap-2 rounded-xl bg-white p-3">
                        <h2 className="font-medium">{t('TalimYonalishlari')}</h2>
                        <div className="rounded-full bg-gray-200 px-1.5 text-sm font-medium">
                          {univerCourse?.length}
                        </div>
                      </div>
                      {univerCourse?.map(course => (
                        <DirectionCard
                          key={course?.id}
                          name={course?.course_name}
                          slug={course?.slug}
                          universityName={university?.name}
                          universitySlug={university?.slug}
                          universityId={university?.id}
                          language={course?.language}
                          study={course?.study}
                          id={course?.id}
                          contractPrice={course?.contract_amount}
                          onOpenModal={onOpenModal}
                          openApplicationModal={() => setOpenApplicationCreate(true)}
                          requirements={course?.requirements}
                          passingScore={course?.direction?.passing_ball}
                          testAvailable={course?.test_available}
                        />
                      ))}
                    </>
                  }
                >
                  <div className="rounded-xl bg-white p-3">
                    <h1 className="font-medium">{t('TalimYonalishlari')}</h1>

                    <EmptyData title={t('malumot_topilmadi')} />
                  </div>
                </Visible>
              }
            >
              <div className="rounded-xl bg-white p-3">
                <h1 className="font-medium">{t('TalimYonalishlari')}</h1>
                <div className="mt-5 space-y-3">
                  {Array.from({ length: 5 }).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Skeleton.Button key={index} active className="!h-12 !w-full !rounded-xl" />
                  ))}
                </div>
              </div>
            </Visible>

            <InfoSection university={university} />

            {university?.longitude && university?.latitude && (
              <div className="rounded-xl bg-white px-2 pb-2">
                <h4 className="mb-2 pt-3 font-medium">{t('oliygoh_manzili')}</h4>

                <YandexMap
                  zoom={19}
                  height="500px"
                  latitude={university?.latitude}
                  longitude={university?.longitude}
                  logoUrl={university?.logo}
                />
              </div>
            )}
          </div>
          <div className="space-y-4 self-start sm:grid sm:grid-cols-2 sm:items-start sm:gap-3 lg:sticky lg:top-4 lg:block">
            <div className="rounded-xl bg-white p-3 shadow-sm">
              <div className="custom-university-image h-40 w-full">
                <Image
                  width="100%"
                  className="!max-h-40 !min-h-40 !w-full !rounded-xl !object-cover"
                  src={university?.image?.replace(/\s+/g, '') || '/images/no-image.webp'}
                  alt={university?.name || 'no-image'}
                  title="Universitet rasmi mavjud emas"
                  placeholder={
                    <div className="flex h-40 w-full items-center justify-center">
                      <Spin />
                    </div>
                  }
                />
              </div>
            </div>
            <MainInformation
              setOpen={setOpen}
              id={university?.id}
              phone={university?.phone}
              testAvailable={testAvailable}
            />
            <div className="shadow-xs rounded-xl bg-white p-3">
              <ContactSection university={university} />
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-40 block flex min-h-20 items-center gap-2 rounded-t-2xl bg-white p-4 md:hidden">
          <Link
            href={`tel:${university?.phone}`}
            target="_blank"
            onClick={onInteractionClick(UserInteraction.PHONE_CLICK)}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gray-100 py-4 text-sm font-medium"
          >
            <CallIcon />

            {t('qongiroq_qilish')}
          </Link>
          <Button
            onClick={onClick}
            className="!w-full !rounded-xl !border-transparent !bg-green-500 !py-6 !text-center !font-semibold !text-white"
          >
            {testAvailable ? t('imtihon_topshirish') : t('ArizaQoldirish')}
          </Button>
        </div>

        <UniversitySelectModal
          universityImage={university?.image}
          universityId={university?.id}
          universityName={university?.name}
          courseId={courseId}
          universityLogo={university?.logo}
          open={open}
          onClose={() => setOpen(false)}
        />
        <ApplicationModal
          universityId={university?.id}
          open={openApplicationCreate}
          onClose={() => setOpenApplicationCreate(false)}
        />
      </div>
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: [universitiesConst.queryKeys.UNIVERSITY_BY_NAME, params?.universityId as string],
    queryFn: getUniversityByName(params?.universityId as string)
  });

  const university: IAllUniversities | undefined = queryClient.getQueryData([
    universitiesConst.queryKeys.UNIVERSITY_BY_NAME,
    params?.universityId as string
  ]);

  await queryClient.prefetchQuery({
    queryKey: [globalConst.queryKeys.GET_UNIVERSITY_COURSE_SELECT, university?.id],
    queryFn: getUniversityCourseSelect({ universityId: university?.id })
  });

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      messages: (await import(`../../../messages/${locale}.json`)).default
    },
    revalidate: 604800,
    notFound: !university
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  };
}
